import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { QairoLogo } from "../components/QairoLogo";
import { formatApiError } from "../lib/formatApiError";
import { API } from "../lib/constants";

const PLANS = [
  { id: "starter", name: "Starter", price: 19, credits: 50, features: ["50 AI generations/month", "Text & Image generation", "Basic templates", "Email support"] },
  { id: "pro", name: "Pro", price: 49, credits: 200, features: ["200 AI generations/month", "All content types", "Advanced templates", "Priority support", "Brand voice settings"], popular: true },
  { id: "business", name: "Business", price: 149, credits: 1000, features: ["1000 AI generations/month", "Unlimited templates", "Custom brand voice", "API access", "Dedicated support"] }
];

export default function PricingPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(null);

  const handleCheckout = async (plan) => {
    if (!user) {
      window.location.href = "/register";
      return;
    }
    setLoading(plan);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        plan,
        origin_url: window.location.origin
      });
      window.location.href = data.url;
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-black">
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
          <Link to="/"><QairoLogo variant="light" /></Link>
          {user ? (
            <Link to="/dashboard" className="text-sm font-medium hover:text-[#0066FF]">Back to Dashboard</Link>
          ) : (
            <Link to="/login" className="text-sm font-medium hover:text-[#0066FF]">Login</Link>
          )}
        </div>
      </header>

      <section className="py-24 px-8 md:px-16">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#0066FF] mb-4 text-center">Pricing</p>
          <h1 className="text-4xl sm:text-5xl tracking-tighter font-black text-center mb-4">Choose Your Plan</h1>
          <p className="text-gray-600 text-center mb-16 max-w-2xl mx-auto">
            All plans include access to GPT-5.2 text generation and Nano Banana image generation.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {PLANS.map((plan) => (
              <div key={plan.id} className={`border-2 ${plan.popular ? 'border-[#0066FF]' : 'border-black'} p-8 relative ${plan.popular ? 'shadow-[8px_8px_0px_0px_rgba(0,47,167,1)]' : ''}`} data-testid={`plan-${plan.id}`}>
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#0066FF] text-white px-4 py-1 text-xs font-bold">MOST POPULAR</div>
                )}
                <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                <div className="mb-6">
                  <span className="text-4xl font-black">${plan.price}</span>
                  <span className="text-gray-500">/month</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-2">
                      <span className="text-[#0066FF] font-bold">+</span>
                      <span className="text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleCheckout(plan.id)}
                  disabled={loading === plan.id}
                  className={`w-full py-3 font-bold transition-all disabled:opacity-50 ${plan.popular ? 'bg-[#0066FF] text-white hover:bg-[#0052CC]' : 'border border-black hover:bg-black hover:text-white'}`}
                  data-testid={`checkout-${plan.id}`}
                >
                  {loading === plan.id ? "Processing..." : user?.subscription === plan.id ? "Current Plan" : "Get Started"}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="py-12 px-8 border-t border-black bg-black text-white">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <QairoLogo variant="dark" />
          <p className="text-sm text-gray-400">&copy; 2026 QAIRO AI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
